import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DropdownItem, IconName, Stack, StackItem } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { TooltippedIconButton } from '@mews-commander/ui';
import { FormSelectField, lt, showErrorAlert, useLocalizeText } from '@mews/framework';
import { getResourceCategoryName } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { useCommanderSelector } from '@mews-commander/utils';
import { AvailabilityAdjustmentFormValues, DeleteResourceCategoryProps } from '../../../../../types';
import { FIELD_ARRAY_AVAILABILITY, RESOURCE_CATEGORY_ID } from '../../../../../constants';
import { useAvailableResourceCategoryOptions } from '../../utils/useAvailableResourceCategoryOptions';
import { BulkAdjustAvailabilitiesDialog } from '../../../../Dialogs/BulkAdjustAvailabilities/BulkAdjustAvailabilitiesDialog';
import { checkPickupInResourceCategory } from '../../../../../selectors';
import { useBlockSetupContext } from '../../../../../contexts/blockSetupContext';

export type EditableCellProps = Omit<DeleteResourceCategoryProps, 'resourceCategoryId'>;

export const EditableCell = ({
    availabilityBlockId,
    serviceId,
    initialResourceCategories,
    fields,
    index,
}: EditableCellProps) => {
    const localizeText = useLocalizeText();
    const { removeRow, setAddResourceCategoryDisabled } = useBlockSetupContext();
    const { watch } = useFormContext<AvailabilityAdjustmentFormValues>();
    const resourceCategoryId = watch(`${FIELD_ARRAY_AVAILABILITY}.${index}.${RESOURCE_CATEGORY_ID}`);

    const [dialogOpened, setDialogOpened] = useState(false);

    const restOptions = useAvailableResourceCategoryOptions(serviceId, index);

    const resourceCategoryName = useCommanderSelector((state) =>
        getResourceCategoryName(state, { id: resourceCategoryId, serviceId })
    );
    const isPickupInResourceCategory = useCommanderSelector((state) =>
        checkPickupInResourceCategory(state, { availabilityBlockId, resourceCategoryId })
    );

    const onResourceCategoryDelete = () => {
        if (isPickupInResourceCategory && initialResourceCategories.some((id) => id === resourceCategoryId)) {
            return showErrorAlert({
                description: lt('NotPossibleDeleteResourceCategoryErrorMessage', {
                    resourceCategoryName: resourceCategoryName ?? '',
                }),
            });
        }
        removeRow(index);
    };

    const openDialog = () => setDialogOpened(true);

    const closeDialog = () => setDialogOpened(false);

    useEffect(() => {
        setAddResourceCategoryDisabled(restOptions.length <= 1);
    }, [restOptions, setAddResourceCategoryDisabled]);

    return (
        <Stack fill itemAlignment="center">
            <StackItem padding="100" data-test-delete-category-id={resourceCategoryId}>
                <TooltippedIconButton
                    size="small"
                    tooltip={TextKey.Delete}
                    icon={IconName.CrossClose}
                    onClick={onResourceCategoryDelete}
                    disabled={fields.length === 1}
                    variant="ghost"
                    type="button"
                />
            </StackItem>

            <StackItem grow>
                <FormSelectField
                    inline
                    required
                    name={`${FIELD_ARRAY_AVAILABILITY}.${index}.${RESOURCE_CATEGORY_ID}`}
                    rules={{
                        required: { message: localizeText({ textKey: TextKey.ValidateRequired }), value: true },
                    }}
                >
                    {restOptions.map((option) => (
                        <DropdownItem
                            {...option}
                            key={option.value}
                            data-test-select-item-value={option.value}
                            label={option.label}
                            value={option.value ?? ''}
                        />
                    ))}
                </FormSelectField>
            </StackItem>

            <StackItem padding="100" data-test-edit-category-id={resourceCategoryId}>
                <TooltippedIconButton
                    size="small"
                    tooltip={TextKey.Edit}
                    onClick={openDialog}
                    icon={IconName.BulkEdit}
                    disabled={!resourceCategoryId}
                    variant="ghost"
                    type="button"
                />
            </StackItem>

            <BulkAdjustAvailabilitiesDialog
                serviceId={serviceId}
                availabilityBlockId={availabilityBlockId}
                resourceCategoryId={resourceCategoryId}
                active={dialogOpened}
                onClose={closeDialog}
                fields={fields}
                rowIndex={index}
            />
        </Stack>
    );
};
